import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackButtonClick, trackContactClick, trackScrollDepth } from "@/utils/analytics";

const SCROLL_THRESHOLDS = [25, 50, 75, 90] as const;

/**
 * Global analytics tracker mounted once in the shared layout.
 *
 * - Reports clicks on any call / WhatsApp / email link (contact_click).
 * - Reports clicks on any element carrying a `data-cta` attribute (cta_click),
 *   using `data-cta-location` for placement context.
 * - Reports scroll depth (scroll_depth) at 25/50/75/90%, reset per page.
 *
 * Individual buttons never need their own handlers — add `data-cta="Label"` in
 * markup and it is tracked automatically.
 */
export function useAnalyticsTracking(): void {
  const location = useLocation();

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;

      // Contact links (tel / WhatsApp / mailto)
      const anchor = target.closest("a");
      const href = anchor?.getAttribute("href") || "";
      if (href) {
        const label =
          anchor?.getAttribute("aria-label") ||
          anchor?.getAttribute("title") ||
          anchor?.textContent?.trim().slice(0, 60) ||
          "";

        if (href.startsWith("tel:")) {
          trackContactClick("phone", label);
        } else if (href.includes("wa.me") || href.includes("api.whatsapp.com")) {
          trackContactClick("whatsapp", label);
        } else if (href.startsWith("mailto:")) {
          trackContactClick("email", label);
        }
      }

      // Explicit CTA buttons
      const ctaEl = target.closest("[data-cta]") as HTMLElement | null;
      if (ctaEl) {
        const ctaLabel = ctaEl.getAttribute("data-cta") || ctaEl.textContent?.trim().slice(0, 60) || "";
        const ctaLocation = ctaEl.getAttribute("data-cta-location") || "";
        trackButtonClick(ctaLabel, ctaLocation);
      }
    };

    document.addEventListener("click", handleClick, true);
    return () => document.removeEventListener("click", handleClick, true);
  }, []);

  // Scroll depth — thresholds reset on every route change.
  useEffect(() => {
    const pagePath = location.pathname;
    const fired = new Set<number>();

    const handleScroll = () => {
      const scrollable = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      const percent = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      SCROLL_THRESHOLDS.forEach((threshold) => {
        if (percent >= threshold && !fired.has(threshold)) {
          fired.add(threshold);
          trackScrollDepth(pagePath, threshold);
        }
      });
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [location.pathname]);
}